/*
 * Two cards on a request (2026-11-03). What they said on the call: the phone company's (Quo's) summary, next steps and every
 * word of each answered call with that phone; Get the call reads calls from before this was built. Walkthrough: plan a
 * visit to see the place before the bid (a day, a time, who goes), shown on the Schedule; notes after it go on the quote.
 */
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import type { CallTalkRow, RequestDetail, WalkthroughRow } from "@steward/shared";
import { api } from "../api/client";
import { useSession } from "../app/session";
import { FormField, InputText } from "../components/fields";
import { clock, dayDate, momentIn, todayIn } from "../components/format";
import { Icon } from "../components/Icon";
import { Modal, useToast } from "../components/overlays";
import { Button, Card } from "../components/parts";
import { errText } from "../components/work";

const minutes = (s: number) => (s < 60 ? "under a minute" : `${String(Math.round(s / 60))} min`);

function CallTalk({ c }: { c: CallTalkRow }) {
  const { company } = useSession();
  const [all, setAll] = useState(false);
  const at = momentIn(c.at, company.timezone);
  return (
    <div className="call-talk">
      <div className="call-talk-head">
        <Icon name="phone" size={16} />
        <b>{c.direction === "in" ? "They called" : "We called"}</b>
        <span className="muted">
          {at.day} at {at.time} · {minutes(c.seconds)}
        </span>
      </div>
      {c.status === "waiting" ? (
        <p className="muted small">The phone company is still writing down this call. Check back in a few minutes.</p>
      ) : c.status === "none" ? (
        <p className="muted small">The phone company has no words for this call.</p>
      ) : (
        <>
          {c.summary.length ? (
            <ul className="call-talk-list">
              {c.summary.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          ) : null}
          {c.nextSteps.length ? (
            <>
              <b className="small">Next steps</b>
              <ul className="call-talk-list">
                {c.nextSteps.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </>
          ) : null}
          {c.words.length ? (
            <Button type="tertiary" size="small" icon={all ? "chevronUp" : "chevronDown"} onClick={() => setAll(!all)}>
              {all ? "Hide the words" : "Show every word"}
            </Button>
          ) : null}
          {all ? (
            <ol className="call-words">
              {c.words.map((w, i) => (
                <li key={i} className={w.who}>
                  <span className="who">{w.who === "them" ? "Them" : "Us"}</span>
                  <span>{w.text}</span>
                </li>
              ))}
            </ol>
          ) : null}
        </>
      )}
    </div>
  );
}

export function CallsCard({ r }: { r: RequestDetail }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const pull = useMutation({
    mutationFn: () => api<{ found: number; calls: CallTalkRow[] }>("POST", `requests/${r.id}/calls`),
    onSuccess: (got) => {
      qc.setQueryData<RequestDetail>(["request", r.id], (old) => (old ? { ...old, calls: got.calls } : old));
      toast(got.found ? `Found ${got.found === 1 ? "1 call" : `${String(got.found)} calls`}` : "No answered calls with them");
    },
    onError: (e) => toast(errText(e), { kind: "error" }),
  });
  const canPull = Boolean(r.phone) && can("lead.update");
  return (
    <Card
      title="What they said on the call"
      action={
        canPull ? (
          <Button type="tertiary" size="small" icon="undo" loading={pull.isPending} onClick={() => pull.mutate()}>
            Get the call
          </Button>
        ) : null
      }
    >
      {r.calls.length ? (
        <div className="stack">
          {r.calls.map((c) => (
            <CallTalk key={c.id} c={c} />
          ))}
        </div>
      ) : (
        <span className="muted">
          {r.phone
            ? "No answered calls with them yet. Get the call reads it from the phone company."
            : "This request has no phone number."}
        </span>
      )}
    </Card>
  );
}

const walksKey = (leadId: string) => ["walkthroughs", "lead", leadId];

/** Plan a walkthrough at their place before the bid, or change one. */
export function WalkthroughCard({ r }: { r: RequestDetail }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({
    queryKey: walksKey(r.id),
    queryFn: () => api<{ walkthroughs: WalkthroughRow[] }>("GET", `walkthroughs?leadId=${r.id}`),
  });
  const [editing, setEditing] = useState<WalkthroughRow | "new" | null>(null);
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: walksKey(r.id) }),
      qc.invalidateQueries({ queryKey: ["walkthroughs", "days"] }),
    ]);
  const callOff = useMutation({
    mutationFn: (id: string) => api("DELETE", `walkthroughs/${id}`),
    onSuccess: () => {
      toast("Walkthrough called off");
      void refresh();
    },
    onError: (e) => toast(errText(e), { kind: "error" }),
  });
  const editable = can("quote.update") && !r.archived;
  const walks = list.data?.walkthroughs ?? [];
  return (
    <Card
      title="Walkthrough"
      flush={walks.length > 0}
      action={
        editable ? (
          <Button type="tertiary" size="small" icon="plus" onClick={() => setEditing("new")}>
            Schedule
          </Button>
        ) : null
      }
    >
      {walks.length ? (
        <div className="todo">
          {walks.map((w) => (
            <div className="item" key={w.id}>
              <Icon name="schedule" />
              <div className="what">
                <b>
                  {dayDate(w.day)}
                  {w.time ? ` at ${clock(w.time)}` : null}
                </b>
                <span>{[w.who ?? "Nobody picked to go", w.notes].filter(Boolean).join(" · ")}</span>
              </div>
              {editable ? (
                <>
                  <Button type="tertiary" size="small" onClick={() => setEditing(w)}>
                    Change
                  </Button>
                  <Button type="tertiary" size="small" loading={callOff.isPending} onClick={() => callOff.mutate(w.id)}>
                    Call off
                  </Button>
                </>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <span className="muted">No walkthrough planned. Schedule one to see the place before you price it.</span>
      )}
      {editing ? (
        <WalkthroughForm
          r={r}
          walk={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => void refresh()}
        />
      ) : null}
    </Card>
  );
}

function WalkthroughForm({
  r,
  walk,
  onClose,
  onSaved,
}: {
  r: RequestDetail;
  walk: WalkthroughRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { company, me } = useSession();
  const toast = useToast();
  const team = useQuery({
    queryKey: ["walkthroughs", "team", company.id],
    queryFn: () => api<{ id: string; name: string }[]>("GET", "walkthroughs/team"),
    staleTime: 60_000,
  });
  const [day, setDay] = useState(walk?.day ?? todayIn(company.timezone));
  const [time, setTime] = useState(walk?.time ?? "10:00");
  const [who, setWho] = useState(walk ? (walk.assignedTo ?? "") : me.id);
  const [address, setAddress] = useState(walk?.address ?? r.address ?? "");
  const [notes, setNotes] = useState(walk?.notes ?? "");
  const save = useMutation({
    mutationFn: () => {
      const body = { day, time: time || null, assignedTo: who || null, address: address.trim() || null, notes };
      return walk
        ? api<WalkthroughRow>("PATCH", `walkthroughs/${walk.id}`, body)
        : api<WalkthroughRow>("POST", "walkthroughs", { ...body, leadId: r.id, clientId: r.clientId });
    },
    onSuccess: () => {
      toast(walk ? "Walkthrough changed" : "Walkthrough on the schedule");
      onSaved();
      onClose();
    },
    onError: (e) => toast(errText(e), { kind: "error" }),
  });
  return (
    <Modal
      title={walk ? "Change the walkthrough" : "Schedule a walkthrough"}
      open
      onClose={onClose}
      footer={
        <Button loading={save.isPending} disabled={!day || !time} onClick={() => save.mutate()}>
          {walk ? "Save" : "Schedule it"}
        </Button>
      }
    >
      <div className="form-grid">
        <InputText label="Day" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
        <InputText label="Time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        <FormField label="Who goes">
          <select className="select" value={who} onChange={(e) => setWho(e.target.value)}>
            <option value="">Nobody yet</option>
            {(team.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </FormField>
        <InputText label="Where" value={address} onChange={(e) => setAddress(e.target.value)} wide />
        <label className="field wide">
          <span className="label">What to look at</span>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>
      <p className="muted small">Nothing is sent to them. Call or text them yourself to set the time.</p>
    </Modal>
  );
}
